/**
 * 推送来的原始数据结构
 */
import { atom, waitForNone } from "recoil";
import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
} from "util/RecoilWithoutCache";
import {
  MixContractFixedPositionInfo,
  MixContractPositionPushInfo,
} from "../entity";
import {
  MixContractHoldSide,
  MixContractMarginMode,
} from "constants/MixContractGlobal";
import { getSymbolPriceBySymbolId } from "./Ticker";
import {
  ContractPositionLevelList,
  getLevel,
  getMixContractInfoBySymbolId,
  MixContractInfoState,
  ContractIsUnionModeSelector,
} from "./Contract";
import { Zero } from "helper/formula/MixContractFormula";
import { Decimal } from "decimal.js";
import {
  getFixedUserAccountByTokenId,
  getTotalUsdtValueAndInquidationPriceCommon,
  MixContractUnionAssetPrice,
} from "./Account";
import {
  FixedOrderMaintainValueStateSelector,
  MixContractCurrentOrderStatisticSelector,
  MixContractTotalOrderValueByUnionKey,
} from "./Order";
import { UserPreferenceSettingState } from "./User";
import { getTokenPriceByTokenId } from "./Token";
import {
  PositionCollector,
  PositionCollectorV1,
  PositionTransfer,
} from "./PositionComputed";
import { PositionModeEnum } from "constants/vars";
import { MixOneWayCalculateOrdersMargin } from "./OneWayPosition";
import { SortItemKey } from "constants/contractPosition";
import { calcCrossMarginEstimateBurstPrice } from "util/formula/futures";
import { groupBy, keyBy, mapValues } from "lodash-es";
import { FuturesIsNewLiquidityState } from "store/data/User";
import StatisticResult from "util/StatisticResult";

// !!! 更新原始数据 set-state
// !!! 读取衍生数据 get-selector

/**
 * websocket推送的原始数据更新到这里
 */
export const MixContractPositionListState = atom<MixContractPositionPushInfo[]>(
  {
    key: "position-list-state-original",
    default: [],
  }
);

/**
 * 当前合约可平的情况
 * 目前一个合约下只有一个保证金，后续增加多币种，因此返回一个以tokenId作为key的map
 * value为{canCloseLong, canCloseShort} 普通委托 闪电委托 可平
 * canPlanCloseLong  计划委托可平 与设置中的 已委托 有关
 */
export const CurrentContractCanCloseSelector = selectorFamily<
  {
    canCloseLong: string;
    canCloseShort: string;
    canPlanCloseLong: string;
    canPlanCloseShort: string;
    curPosManyInfo: any;
    curPosEmptyInfo: any;
  },
  string
>({
  key: "current-ct-can-close",
  get:
    (tokenId: string) =>
    ({ get }) => {
      const contractConfig = get(MixContractInfoState);
      const positionList = get(MixContractPositionListState);
      const userPreferenceSetting = get(UserPreferenceSettingState);
      let returnData = {
        canCloseLong: "0", // 平多  卖出
        canCloseShort: "0", // 平空   买入
        canPlanCloseLong: "0",
        canPlanCloseShort: "0",
        curPosManyInfo: {}, // 平多信息
        curPosEmptyInfo: {}, // 空信息
      };
      positionList.forEach((p) => {
        if (p.symbolId === contractConfig.symbolId && p.tokenId === tokenId) {
          const count = userPreferenceSetting.quantityIncludesDelegated
            ? new Decimal(p.available).add(p.locked)
            : p.available;

          // 多仓  平多
          if (p.holdSide === MixContractHoldSide.LONG_POSITIONS) {
            returnData.canCloseLong = new Decimal(count).toFixed(
              contractConfig.placeConfig.volumePlace,
              Decimal.ROUND_DOWN
            );
            returnData.canPlanCloseLong = new Decimal(count).toFixed(
              contractConfig.placeConfig.volumePlace,
              Decimal.ROUND_DOWN
            );
            returnData.curPosManyInfo = JSON.parse(JSON.stringify(p));
          }
          // 空仓    平空
          else {
            returnData.canCloseShort = new Decimal(count).toFixed(
              contractConfig.placeConfig.volumePlace,
              Decimal.ROUND_DOWN
            );
            returnData.canPlanCloseShort = new Decimal(count).toFixed(
              contractConfig.placeConfig.volumePlace,
              Decimal.ROUND_DOWN
            );
            returnData.curPosEmptyInfo = JSON.parse(JSON.stringify(p));
          }
        }
      });

      return returnData;
    },
});

/**
 * 基本计算的仓位信息：没有任何的聚合操作
 */
export const MixContractBaseFixedPositionListSelector = selector<
  MixContractFixedPositionInfo[]
>({
  key: "base-fixed-position-list-selector",
  get: ({ get }) => {
    // 原始仓位信息
    const positionList = get(MixContractPositionListState);
    const transfer = get(PositionTransfer);

    // 基本附加信息
    return positionList.map<MixContractFixedPositionInfo>(transfer);
  },
});

/**
 * base方法 计算仓位的减仓价
 *
 * 直接在开仓 弹窗   调用此方法   封装hooks即可
 * get(MixContractBasePositionListSelector)([positionList, 开仓item数据])
 *
 * 弹窗 爆仓价格  新增需求  魏兵兵
 */

export const MixContractBasePositionListSelector = selector({
  key: "base-position-list-selector",
  get: ({ get }) => {
    // 这块需要新写  只计算了现有仓位一些信息
    // 下面的 283  285
    // const collector = get(PositionCollector)
    const collector = get(PositionCollectorV1);
    //这里 tokenIdTotalValue 只能取到真实仓位 不包含即将开仓 所以用下面 computeInquidationPriceFn 来获取包含将要开仓的部分的总币对当前价值
    const computeInquidationPriceFn = get(
      getTotalUsdtValueAndInquidationPriceCommon
    ); // 计算价值用  Account.ts

    const { tokenTotalCrossedOpenMaintain, tokenSymbolCrossedOpenMaintain } =
      get(MixContractCurrentOrderStatisticSelector);

    const tokenTotalFixedOrderMaintain = get(
      FixedOrderMaintainValueStateSelector
    );

    // 单向持仓相关 代码
    const positionModeSet = get(UserPreferenceSettingState).positionModeSet;
    const { tokenIdFixedTotalMargin } = get(MixOneWayCalculateOrdersMargin);
    // 仓位聚合的数据
    const { tokenTotalFixedPositionMargin } = get(
      MixContractPositionStatisticSelector
    );
    // 新开仓位 + 现有仓位 list  或  现有仓位list

    const isNewLiquidity = get(FuturesIsNewLiquidityState);

    // 联合保证金相关计算
    const { unionAssetAvailable } = get(MixContractUnionAssetPrice);
    const isUnionMode = get(ContractIsUnionModeSelector);

    return (
      positionList: MixContractFixedPositionInfo[],
      computedFeePrice = 0 // 需要扣掉的手续费
    ) => {
      // 计算  现有仓位+即将开仓的    usdt价值   computeInquidationPriceFn 这介从Account.ts里面拿
      // 这里新增币对当前价值: 所有全仓币对当前价值*(MM+爆仓手续费) 不包含逐仓

      const {
        tokenSymbolHoldUsdtValue,
        tokenSymbolHoldUsdtValueForMM,
        tokenSymbolSideUsdtValue,
        tokenSideUsdtValue,
        tokenIdTotalValue,
        tokenIdTotalValueMax,
        tokenIdTotalValueMaxForMM,
      } = computeInquidationPriceFn({
        positionList: positionList,
      });

      const {
        tokenTotalNetValue, //  统计某保证金下所有合约的净头寸价值
        tokenSymbolNetValue, // 统计某保证金下的某个合约的净头寸价值
        tokenCrossedMaintain, //统计某保证金下所有交易对大头仓位的维持保证金之和
        tokenSymbolCrossedMaintain, // 某币对维持保证金
        tokenSymbolNetHold, //统计净头寸数量
        tokenTotalCrossedPositionUnrealizedProfit, // 统计全仓仓位未实现盈亏
      } = collector(
        positionList,
        tokenSymbolHoldUsdtValueForMM,
        tokenSymbolSideUsdtValue,
        tokenSideUsdtValue
      ); // collector    PositionComputed.ts    PositionCollectorV1

      const positionsByUnionKey = mapValues(
        groupBy(positionList, (p) => `${p.tokenId}_${p.symbolId}`),
        (positions) => keyBy(positions, "holdSide")
      );

      // let finalNetHoldSum = Zero
      // tokenSymbolNetHold.forEach((dValue, symbolId) => {
      //     finalNetHoldSum = finalNetHoldSum.add(dValue)
      // })

      // 仅处理全仓仓位计算减仓价
      return positionList.map<MixContractFixedPositionInfo>((position) => {
        const { tokenId, symbolId, marginMode, dReducePrice, holdSide } =
          position;
        const contractConfig = get(getMixContractInfoBySymbolId(symbolId));
        const contractLevelList = get(
          ContractPositionLevelList(contractConfig.symbolId)
        );
        let dNewReducePrice = dReducePrice; // 这个爆仓价值 默认 0 下面会根据 净头寸再算一下
        let mm = position.mm;
        let level = position.level;
        // 仅处理全仓仓位
        if (marginMode === MixContractMarginMode.CROSS_MARGIN) {
          // 保证金的资产
          const account = get(getFixedUserAccountByTokenId(tokenId));
          // 保证金的标记价格
          const bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;
          // 合约的标记价格
          const fairPrice = get(getSymbolPriceBySymbolId(symbolId)).markPrice;
          // 联合查询Key
          const unionKey = `${tokenId}_${symbolId}`;
          // 总资产余额
          // console.log('account.available', account.available)
          let dAvailable = new Decimal(account.available).sub(
            new Decimal(computedFeePrice)
          ); // 扣除手续费

          // todo 后续若开放其他业务线，则需要将 ['USDT', 'SUSDT'] 的判断去除
          // 只有保证金为 USDT 和 SUSDT
          if (["USDT", "SUSDT"].includes(tokenId)) {
            dAvailable = dAvailable.add(unionAssetAvailable);
          }

          /** 全仓总未实现盈亏 */
          const dTotalCrossedUpnl =
            tokenTotalCrossedPositionUnrealizedProfit.get(tokenId);
          // 全仓总开仓维持保证金
          const dTotalOpenMaintain = tokenTotalCrossedOpenMaintain.get(tokenId);
          // 总共持仓价值，包含多空两仓，以及委托  改造后的
          const totalUsdtValue = tokenSymbolHoldUsdtValue.get(unionKey);
          const totalUsdtValueForMM =
            tokenSymbolHoldUsdtValueForMM.get(unionKey);

          // 根据 仓位(现有即将) 算手续费率 级别
          const levelInfo = getLevel(
            get(ContractPositionLevelList(symbolId)),
            totalUsdtValue.toNumber()
          );

          // 根据 仓位(现有即将) 算手续费率 级别
          const levelInfoForMM = getLevel(
            get(ContractPositionLevelList(symbolId)),
            totalUsdtValueForMM.toNumber()
          );

          mm = levelInfoForMM.keepMarginRate;
          level = levelInfo.level;

          // 净头寸
          const netHold = tokenSymbolNetHold.get(unionKey);

          // 计算w：资金分配权重，为该交易对下净持仓，都是保证金计算，w结果不带单位
          const symbolNetValue = tokenSymbolNetValue.get(unionKey); // 某个合约的净头寸价值
          const totalNetValue = tokenTotalNetValue.get(tokenId); // 所有合约的净头寸价值

          // 逐仓仓位保证金
          const totalFixedMargin = tokenTotalFixedPositionMargin.get(tokenId);
          let omega = Zero; // 公式中的 wi
          if (!totalNetValue.isZero()) {
            omega = symbolNetValue.div(totalNetValue).abs();
          }

          // 得到保证金下所有合约大头仓位维持保证金的和(保证金记)
          const totalMaintain = tokenCrossedMaintain.get(tokenId);

          // 保证金所开逐仓委托对应的维持保证金
          const fixedOrderMaintain = tokenTotalFixedOrderMaintain.get(tokenId);

          // 计算维持价格
          let assetPart = Zero;
          // debugger
          if (isNewLiquidity) {
            if (positionModeSet === PositionModeEnum.ONE_WAY || isUnionMode) {
              let final = tokenIdTotalValueMax.get(unionKey);
              let finalForMM = tokenIdTotalValueMaxForMM.get(unionKey);
              let levelInfoForMM = getLevel(
                contractLevelList,
                finalForMM.toNumber()
              );
              mm = levelInfoForMM.keepMarginRate;
              let levelInfo = getLevel(contractLevelList, final.toNumber());
              level = levelInfo.level;
              // 所有全仓币对维持保证金和
              let allCrossMariginValue = tokenIdTotalValue.get(tokenId).div(bp);
              const maintainPart2 = account.maintainPart2 || Zero;
              allCrossMariginValue = Decimal.max(
                allCrossMariginValue,
                maintainPart2
              );
              assetPart = dAvailable
                .add(dTotalCrossedUpnl) // 全仓总未实现盈亏
                .add(totalFixedMargin)
                .sub(tokenIdFixedTotalMargin.get(tokenId)) // 与双向持仓不一样 //逐仓币对占用仓位保证金
                .sub(allCrossMariginValue) // 所有全仓币对维持保证金和
                // 全仓单向令 omega 为 1
                // .times(omega)
                .times(bp);

              // 上面算出的结果  再区分方向  用 标记价格(fairPrice) +- assetPart
              if (!netHold.isZero()) {
                // 根据头寸方向分别计算
                if (netHold.greaterThan(Zero)) {
                  // 头寸为多头
                  dNewReducePrice = new Decimal(fairPrice).sub(
                    assetPart.div(netHold.abs())
                  );
                } else {
                  // 头寸为空头
                  dNewReducePrice = new Decimal(fairPrice).add(
                    assetPart.div(netHold.abs())
                  );
                }
              }
            } else {
              const positions = positionsByUnionKey[unionKey];
              const longPosition =
                positions[MixContractHoldSide.LONG_POSITIONS];
              const shortPosition =
                positions[MixContractHoldSide.SHORT_POSITIONS];
              dNewReducePrice = calcCrossMarginEstimateBurstPrice({
                available: dAvailable, // 总资产余额
                othersUpnl: dTotalCrossedUpnl
                  .sub(longPosition?.dUpnl ?? 0)
                  .sub(shortPosition?.dUpnl ?? 0), // 其他全仓币对的未实现盈亏
                othersMaintain: totalMaintain
                  .sub(tokenSymbolCrossedMaintain.get(unionKey) ?? 0)
                  .add(
                    dTotalOpenMaintain.sub(
                      tokenSymbolCrossedOpenMaintain.get(unionKey) ?? 0
                    )
                  )
                  .add(fixedOrderMaintain), // 其他币对的维持保证金
                bp: new Decimal(bp), // 指数价格, u本位为 1 ？
                longOpenPrice: new Decimal(longPosition?.averageOpenPrice ?? 0), // 该币对的多头开仓价
                longOpenCount: longPosition?.dHold ?? Zero, // 该币对的多头开仓数量
                shortOpenPrice: new Decimal(
                  shortPosition?.averageOpenPrice ?? 0
                ), // 该币对的空头开仓价
                shortOpenCount: shortPosition?.dHold ?? Zero, // 该币对的空头开仓数量
                totalDelegateValue: get(
                  MixContractTotalOrderValueByUnionKey(unionKey)
                ), // 该币对总委托价值
                mm: new Decimal(mm), // 维持保证金率
                feeRate: new Decimal(contractConfig.feeRate), // 手续费率
              });
            }
          } else {
            //开关不开 继续使用之前的计算公式
            if (positionModeSet === PositionModeEnum.ONE_WAY) {
              let final = tokenIdTotalValueMax.get(unionKey);
              let finalForMM = tokenIdTotalValueMaxForMM.get(unionKey);
              let levelInfoForMM = getLevel(
                contractLevelList,
                finalForMM.toNumber()
              );
              mm = levelInfoForMM.keepMarginRate;
              let levelInfo = getLevel(contractLevelList, final.toNumber());
              level = levelInfo.level;
              // 所有全仓币对维持保证金和
              let allCrossMariginValue = tokenIdTotalValue.get(tokenId).div(bp);
              assetPart = dAvailable
                .add(dTotalCrossedUpnl) // 全仓总未实现盈亏
                .add(totalFixedMargin)
                .sub(tokenIdFixedTotalMargin.get(tokenId)) // 与双向持仓不一样 //逐仓币对占用仓位保证金
                .sub(allCrossMariginValue) // 所有全仓币对维持保证金和
                .times(omega)
                .times(bp);
            } else {
              assetPart = dAvailable
                .add(dTotalCrossedUpnl) // 全仓总未实现盈亏
                .sub(totalMaintain) // 维持保证金的和
                .sub(dTotalOpenMaintain) // 全仓冻结 维持保证金
                .sub(fixedOrderMaintain) // 逐仓冻结  维持保证金
                .times(omega)
                .times(bp);
            }

            // 上面算出的结果  再区分方向  用 标记价格(fairPrice) +- assetPart
            if (!netHold.isZero()) {
              // 根据头寸方向分别计算
              if (netHold.greaterThan(Zero)) {
                // 头寸为多头
                dNewReducePrice = new Decimal(fairPrice).sub(
                  assetPart.div(netHold.abs())
                );
              } else {
                // 头寸为空头
                dNewReducePrice = new Decimal(fairPrice).add(
                  assetPart.div(netHold.abs())
                );
              }
            }
          }
        }

        //减仓价是否安全
        // (FIXED_MARGIN = 1), // 逐仓模式
        // (CROSS_MARGIN = 2) // 全仓模式
        let isSafe: boolean;
        let dReducePriceValue = "";
        // 逐仓模式 下面代码可以去掉 因为在transfer中已经处理
        if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
          // 多
          if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
            isSafe = dNewReducePrice.lessThanOrEqualTo(
              contractConfig.placeConfig.longLimitBurstPrice
            );
          }
          // 空
          else {
            isSafe =
              dNewReducePrice.greaterThanOrEqualTo(
                contractConfig.placeConfig.shortLimitBurstPrice
              ) || dNewReducePrice.isNegative();
          }
        }
        // 全仓模式
        else {
          isSafe =
            dNewReducePrice.lessThanOrEqualTo(
              contractConfig.placeConfig.longLimitBurstPrice
            ) ||
            dNewReducePrice.greaterThanOrEqualTo(
              contractConfig.placeConfig.shortLimitBurstPrice
            );
        }

        if (!isSafe) {
          dReducePriceValue = dNewReducePrice.toFixed(
            contractConfig.placeConfig.pricePlace,
            holdSide === MixContractHoldSide.LONG_POSITIONS
              ? Decimal.ROUND_UP
              : Decimal.ROUND_DOWN
          );
        } else {
          // 永不爆仓
        }

        return {
          ...position,
          dReducePrice: dNewReducePrice,
          isSafe,
          dReducePriceValue, // 如果永不爆仓这是空值
          mm,
          level,
        };
      });
    };
  },
});

/**
 * 仓位统计
 */
export const MixContractPositionStatisticSelector = selector({
  key: "position-statistic-selector",
  get: ({ get }) => {
    const positionList = get(MixContractBaseFixedPositionListSelector);
    const collector = get(PositionCollector);

    return collector(positionList);
  },
});

/**
 * 为仓位计算减仓价
 */
export const MixContractFixedPositionListSelector = selector<
  MixContractFixedPositionInfo[]
>({
  key: "fixed-position-list-selector",
  get: ({ get }) => {
    const positionList = get(MixContractBaseFixedPositionListSelector);

    return get(MixContractBasePositionListSelector)(positionList);
  },
});

export const MixContractPositionSortState = atom<SortItemKey>({
  key: "ct-position-sort-selector",
  default: SortItemKey.default_sort,
});

/**
 * 当前持仓的显示方式  持仓数量 还是持仓价值
 */
export enum MixContractHoldType {
  /** 显示持仓量 */
  HOLD_TYPE_NUM = 0,
  /** 显示持仓价值 */
  HOLD_TYPE_VALUE = 1,
}

export const MixContractPositionHoldState = atom<MixContractHoldType>({
  key: "ct-position-hold-ui-state",
  default: MixContractHoldType.HOLD_TYPE_NUM,
});

/**
 * 全部全仓仓位净头寸是否为0
 */
export const MixContractPositionNetHoldIsZeroSelector = selector({
  key: "position-nethold-is-zero-selector",
  get: ({ get }) => {
    const positionList = get(MixContractBaseFixedPositionListSelector);
    let tokenSymbolNetHold = new StatisticResult();
    let bHasCrossPos = false;
    for (let position of positionList) {
      const { tokenId, symbolId, holdSide, available, locked, marginMode } =
        position;
      const dHold = new Decimal(available).add(locked);
      const unionKey = `${tokenId}_${symbolId}`;
      // 区分逐仓全仓的统计
      if (marginMode === MixContractMarginMode.CROSS_MARGIN) {
        bHasCrossPos = true;
        tokenSymbolNetHold.add(
          unionKey,
          dHold.times(holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1)
        );
      }
    }
    let bIsZero = true;
    //如果没有全仓仓位直接返回false
    if (!bHasCrossPos) {
      return false;
    }
    tokenSymbolNetHold.forEach((dValue, symbolId) => {
      if (!dValue.isZero()) bIsZero = false;
    });
    return bIsZero;
  },
});

/**
 * 全部全仓仓位净头寸是否为0
 */
export const MixContractPositionNetHoldStatistics = (
  positionList: any[],
  specialTokenId: string
) => {
  // const positionList = get(MixContractBaseFixedPositionListSelector)
  let tokenSymbolNetHold = new StatisticResult();
  let bHasCrossPos = false;
  for (let position of positionList) {
    const { tokenId, symbolId, holdSide, available, locked, marginMode } =
      position;
    const dHold = new Decimal(available).add(locked);
    const unionKey = `${tokenId}_${symbolId}`;
    // 区分逐仓全仓的统计
    if (
      marginMode === MixContractMarginMode.CROSS_MARGIN &&
      specialTokenId === tokenId
    ) {
      bHasCrossPos = true;
      tokenSymbolNetHold.add(
        unionKey,
        dHold.times(holdSide === MixContractHoldSide.LONG_POSITIONS ? 1 : -1)
      );
    }
  }
  let bIsZero = true;
  //如果没有全仓仓位直接返回false
  if (!bHasCrossPos) {
    return false;
  }
  tokenSymbolNetHold.forEach((dValue, symbolId) => {
    if (!dValue.isZero()) bIsZero = false;
  });
  return bIsZero;
};
